import React, { useState, useEffect } from 'react';
import { X, Award, Calendar, User, Heart, CheckCircle, FileText, XCircle, Loader2 } from 'lucide-react';

interface SacramentApprovalsModalProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string | number;
  user: any; // 🟢 ADD THIS LINE TO THE PROPS INTERFACE
}

export const SacramentApprovalsModal: React.FC<SacramentApprovalsModalProps> = ({ isOpen, onClose, shopId, user }) => {
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [actioningId, setActioningId] = useState<number | null>(null);
 
  const [reviewingApp, setReviewingApp] = useState<any | null>(null);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/fetch-church-sacrament-applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop_id: shopId, status: 'Pending' })
      });
      const data = await response.json();
      setApplications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading sacraments:", err);
    } finally {
      setLoading(false);
    }
  };

  // UPDATED: Now triggers specifically when the modal transitions to an open state
  useEffect(() => {
    if (isOpen && shopId) {
      fetchApplications();
    }
  }, [isOpen, shopId]); // <-- Added isOpen to dependencies

  // MOVED: Place the conditional layout gate right here before rendering the template view
  if (!isOpen) return null;

  const handleUpdateStatus = async (id: number, targetStatus: 'Approved' | 'Declined') => {
    let reasonText = null;
    if (targetStatus === 'Declined') {
      const inputReason = prompt("Reason for application rejection:");
      if (inputReason === null) return;
      if (!inputReason.trim()) {
        alert("A reason is required to reject an intake application.");
        return;
      }
      reasonText = inputReason.trim();
    }

    // Locates the clicked row from your local state array cleanly
    const currentApp = applications.find(item => item.id === id);
    if (!currentApp) {
      alert("Application data context lost. Please refresh the dashboard.");
      return;
    }

    setActioningId(id);
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/church-update-sacrament', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: id,
          status: targetStatus,
          shop_id: shopId,
          rejection_reason: reasonText,
          // 🟢 KEYS PERFECTLY MATCHING YOUR ATTACHED SCHEMA
          member_id: currentApp.member_id, // e.g., 104
          actioned_by: user?.username || 'Admin Staff', // Active approver name
          candidate_name: currentApp.candidate_name,
          sacrament: currentApp.sacrament,
          father_name: currentApp.father_name,
          mother_name: currentApp.mother_name,
          // Dynamic fallback if phone number is joined inside your component state
          phone_number: currentApp.phone_number || currentApp.member_phone || ''
        })
      });

      if (response.ok) {
        alert(`Application successfully ${targetStatus.toLowerCase()}!`);
        setApplications(prev => prev.filter(item => item.id !== id));
      } else {
        throw new Error("Server error");
      }
    } catch (err) {
      alert("Failed to update record state.");
    } finally {
      setActioningId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-2xl rounded-[2rem] shadow-2xl overflow-hidden max-h-[85vh] flex flex-col text-left">
        
        {/* Header */}
        <div className="p-6 bg-blue-600 text-white flex justify-between items-center">
          <div>
            <h3 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
              <Award size={22} /> Baptism Approvals
            </h3>
            <p className="text-xs text-blue-100 uppercase font-medium mt-0.5">Pending Church Intake Verification</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-blue-500 rounded-full transition-colors">
            <X size={20}/>
          </button>
        </div>

        {/* Applications List Area */}
        <div className="p-6 flex-1 overflow-y-auto space-y-6 bg-gray-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2">
              <Loader2 className="animate-spin text-blue-600" size={32} />
              <p className="text-xs font-bold uppercase tracking-wider">Loading Requests...</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-sm font-bold uppercase tracking-wider">No pending applications found.</p>
            </div>
          ) : (
            applications.map((currentApp) => {
              // Simple, unbreakable flat assignments mapped by n8n
              const dynamicChurchName = currentApp?.church_name || "CANDIDATE RECORD";
              const candidateFullName = currentApp?.candidate_name || "N/A";
              const dob = currentApp?.dob || "N/A";
              const residentialAddress = currentApp?.residential_address || "N/A";
  
              const poBox = currentApp?.po_box || "N/A";
              const officeTel = currentApp?.office_tel || "N/A";
              const houseTel = currentApp?.house_tel || "N/A";
              const attendsRegularly = currentApp?.attends_regularly || "N/A";

              const fatherName = currentApp?.father_name || "N/A";
              const motherName = currentApp?.mother_name || "N/A";

              const sponsor1 = currentApp?.sponsor_name_1 || "N/A";
              const isAnglican1 = currentApp?.sponsor_is_anglican_1 || "N/A";
              const sponsor2 = currentApp?.sponsor_name_2 || "N/A";
              const isAnglican2 = currentApp?.sponsor_is_anglican_2 || "N/A";

              return (
                <div key={currentApp.id} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row justify-between gap-6 items-start">
                  
                  {/* Detailed Information Grid */}
                  <div className="flex-1 space-y-4 w-full">
                    <div>
                      <span className="inline-block bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-md mb-2">
                        {currentApp?.sacrament || 'BAPTISM'}
                      </span>
                      <h4 className="text-base font-black text-gray-800 uppercase tracking-wide border-b pb-1">
                        {dynamicChurchName}
                      </h4>
                    </div>

                    {/* Grid Columns layout */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Candidate Column */}
                      <div className="space-y-1 text-sm font-medium text-gray-700 bg-gray-50 p-3.5 rounded-xl">
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-1">1. Candidate Info</p>
                        <div><span className="text-xs font-bold text-gray-400 uppercase">Full Name:</span> {candidateFullName}</div>
                        <div><span className="text-xs font-bold text-gray-400 uppercase">DOB:</span> {dob}</div>
                        <div><span className="text-xs font-bold text-gray-400 uppercase">Residence:</span> {residentialAddress}</div>
                        <div><span className="text-xs font-bold text-gray-400 uppercase">P.O. Box:</span> {poBox}</div>
                        <div className="text-xs pt-1 border-t border-dashed border-gray-200 flex gap-4 text-gray-500 mt-1">
                          <span>Off: {officeTel}</span>
                          <span>House: {houseTel}</span>
                        </div>
                        <div className="pt-1.5">
                          <span className="text-xs font-bold text-gray-400 uppercase">Regular Attendee: </span>
                          <span className={`text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-md ${attendsRegularly === 'yes' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                            {attendsRegularly}
                          </span>
                        </div>
                      </div>

                      {/* Family/Sponsors Column */}
                      <div className="space-y-3.5">
                        {/* Parents nested */}
                        <div className="space-y-1 text-sm font-medium text-gray-700 bg-gray-50 p-3 rounded-xl">
                          <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-1">2. Parents Info</p>
                          <div><span className="text-xs font-bold text-gray-400 uppercase">Father:</span> {fatherName}</div>
                          <div><span className="text-xs font-bold text-gray-400 uppercase">Mother:</span> {motherName}</div>
                        </div>

                        {/* Sponsors nested */}
                        <div className="space-y-1 text-sm font-medium text-gray-700 bg-gray-50 p-3 rounded-xl">
                          <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-1">3. Witnesses / Sponsors</p>
                          <div className="border-b border-dashed border-gray-200 pb-1 mb-1">
                            <span className="text-xs font-bold text-gray-400 uppercase">S1:</span> {sponsor1} 
                            <span className="text-[10px] ml-1.5 font-extrabold uppercase text-gray-500">(Anglican: {isAnglican1})</span>
                          </div>
                          <div>
                            <span className="text-xs font-bold text-gray-400 uppercase">S2:</span> {sponsor2} 
                            <span className="text-[10px] ml-1.5 font-extrabold uppercase text-gray-500">(Anglican: {isAnglican2})</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions Column */}
                  <div className="flex md:flex-col gap-2 w-full md:w-auto md:self-stretch justify-end md:justify-center border-t md:border-t-0 pt-3 md:pt-0 border-gray-100">
                    <button
                      onClick={() => handleUpdateStatus(currentApp.id, 'Approved')}
                      disabled={actioningId !== null}
                      className="flex-1 md:flex-initial bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-3 rounded-xl flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
                    >
                      {actioningId === currentApp.id ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle size={14} />} Approve
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(currentApp.id, 'Declined')}
                      disabled={actioningId !== null}
                      className="flex-1 md:flex-initial bg-red-600 hover:bg-red-700 text-white font-bold px-4 py-3 rounded-xl flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all"
                    >
                      {actioningId === currentApp.id ? <Loader2 className="animate-spin" size={14} /> : <XCircle size={14} />} Decline
                    </button>
                    
                    {/* NEW! THIRD BUTTON: REVIEW RECORD */}
                    <button onClick={() => setReviewingApp(currentApp)} className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold px-4 py-2.5 rounded-xl flex items-center justify-center gap-2 text-xs uppercase tracking-wider transition-all border border-slate-200" >
                      <FileText size={14} /> Review Record
                    </button>

                  </div>

                </div>
              );
            })
          )}
        </div>

        {/* READ-ONLY REVIEW MODAL SCREEN OVERLAY */}
        {reviewingApp && (
          <div className="absolute inset-0 bg-white z-50 flex flex-col animate-in slide-in-from-right duration-200 text-slate-800">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center flex-shrink-0">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest px-2 py-0.5 bg-blue-500 rounded text-white">
                  {reviewingApp?.sacrament || 'BAPTISM'} FILE
                </span>
                <h3 className="text-lg font-black uppercase tracking-tight mt-1">
                  {reviewingApp?.candidate_name || 'Review Intake Record'}
                </h3>
              </div>
              <button onClick={() => setReviewingApp(null)} className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors text-white">
                <X size={20}/>
              </button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6 bg-slate-50 text-xs">
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
                <div>
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-2">1. Master Candidate Record</p>
                  <div className="grid grid-cols-2 gap-y-2 gap-x-4 border-b pb-3 border-dashed">
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Full Name</span> <p className="font-semibold text-slate-800 text-sm mt-0.5">{reviewingApp?.candidate_name || "N/A"}</p></div>
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Date of Birth</span> <p className="font-semibold text-slate-800 text-sm mt-0.5">{reviewingApp?.dob || "N/A"}</p></div>
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Residence</span> <p className="font-semibold text-slate-800 text-sm mt-0.5">{reviewingApp?.residential_address || "N/A"}</p></div>
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">P.O. Box</span> <p className="font-semibold text-slate-800 text-sm mt-0.5">{reviewingApp?.po_box || "N/A"}</p></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-3 text-slate-700">
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Office Tel</span> {reviewingApp?.office_tel || "N/A"}</div>
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">House Tel</span> {reviewingApp?.house_tel || "N/A"}</div>
                    <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Regularity Flag</span> <span className="font-bold text-blue-600 uppercase">{reviewingApp?.attends_regularly || "N/A"}</span></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-2">2. Biological Parents Info</p>
                  <div><span className="font-bold text-slate-400 uppercase block text-[10px]">Father's Full Name</span> <p className="font-semibold text-slate-800 mt-0.5">{reviewingApp?.father_name || "N/A"}</p></div>  
                  <div className="pt-2"><span className="font-bold text-slate-400 uppercase block text-[10px]">Mother's Full Name</span> <p className="font-semibold text-slate-800 mt-0.5">{reviewingApp?.mother_name || "N/A"}</p></div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
                  <p className="text-[10px] font-black text-blue-600 uppercase tracking-wider mb-2">3. Witnesses & Godparents</p>
                  <div className="border-b border-dashed pb-2">
                    <span className="font-bold text-slate-400 uppercase block text-[10px]">First Witness (S1)</span>
                    <p className="font-semibold text-slate-800 mt-0.5">{reviewingApp?.sponsor_name_1 || "N/A"}</p>
                    <span className="text-[10px] font-bold text-slate-500">Anglican Communicant: {reviewingApp?.sponsor_is_anglican_1 || "N/A"}</span>
                  </div>
                  <div className="pt-1">
                    <span className="font-bold text-slate-400 uppercase block text-[10px]">Second Witness (S2)</span>
                    <p className="font-semibold text-slate-800 mt-0.5">{reviewingApp?.sponsor_name_2 || "N/A"}</p>
                    <span className="text-[10px] font-bold text-slate-500">Anglican Communicant: {reviewingApp?.sponsor_is_anglican_2 || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 bg-slate-100 border-t flex justify-end flex-shrink-0">
              <button onClick={() => setReviewingApp(null)} className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-colors">
                Close Inspection
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

